import type { MyTaskList, ProjectDetail, TaskPerson, TaskStatus, TaskSummary } from "@esi/shared";
import { useQueryClient } from "@tanstack/react-query";
import { ListTodo, Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { EmptyState } from "@/components/EmptyState";
import { Notice } from "@/components/Notice";
import { Page } from "@/components/Page";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useDebounced } from "../conversation/ConversationList.tsx";
import { Link, PROJECTS_PATH, projectTaskPath } from "../router.tsx";
import { AssigneePicker } from "./AssigneePicker.tsx";
import {
  DueDate,
  RowStatusLabel,
  RowStatusPicker,
  StatusFilterField,
  TaskError,
  TaskHeading,
  TaskSelect,
  writeError,
} from "./parts.tsx";
import {
  type RowStatusChange,
  type StatusFilter,
  statusMatches,
  taskKeys,
  taskTextRedacted,
  UnsupportedOpenError,
  usePendingRowStatus,
  useRowStatus,
  useTaskPages,
} from "./queries.ts";
import { TaskEditor } from "./TaskEditor.tsx";

export function MyTasksPage() {
  const { t } = useTranslation();
  useDocumentTitle(t("tasks.myTitle"));
  return (
    <Page>
      <PageHeader title={t("tasks.myTitle")} />
      <TaskList key="my" />
    </Page>
  );
}
export function ProjectTasks({ project }: { project: ProjectDetail }) {
  return <TaskList key={project.id} project={project} />;
}
function TaskList({ project }: { project?: ProjectDetail }) {
  const { t } = useTranslation();
  useDocumentTitle(project?.name, Boolean(project));
  const client = useQueryClient();
  const [status, setStatus] = useState<StatusFilter>("open");
  const [scope, setScope] = useState("assigned");
  const [assignee, setAssignee] = useState("all");
  const [choosingPerson, setChoosingPerson] = useState(false);
  const [person, setPerson] = useState<TaskPerson | null>(null);
  const [q, setQ] = useState("");
  const search = useDebounced(q);
  const [create, setCreate] = useState(false);
  const guest = project?.role === "guest";
  const filters = {
    status,
    ...(project
      ? {
          ...(search.trim() && { q: search.trim() }),
          ...(!guest &&
            assignee !== "all" && { assignee: assignee === "person" ? person?.userId : assignee }),
        }
      : { createdByMe: scope === "created" }),
  };
  const list = useTaskPages(project?.id ?? null, filters);
  const pages = list.data?.pages ?? [];
  const pending = usePendingRowStatus();
  // A row whose saved status left the filter goes at once (tasks-quick-status); one still saving
  // stays, busy, until the server answers. Server order and filtering are otherwise untouched.
  const tasks = pages
    .flatMap((page) => page.tasks)
    .filter((task) => statusMatches(status, task.status) || pending.has(task.id));
  const rowStatus = useRowStatus();
  const listRef = useRef<HTMLDivElement>(null);
  // The filter and pending rows as they are when a write settles, not when it started.
  const statusNow = useRef(status);
  statusNow.current = status;
  const pendingNow = useRef(pending);
  pendingNow.current = pending;
  const [focusAfter, setFocusAfter] = useState<string | null>(null);
  useEffect(() => {
    if (focusAfter === null) {
      return;
    }
    setFocusAfter(null);
    // Only focus the departed picker held: where the user has gone since, they stay.
    const active = document.activeElement;
    if (active && active !== document.body && active.isConnected) {
      return;
    }
    const root = listRef.current;
    // The next row's title, else the previous one's, else the list's heading (My tasks: the
    // page's), else the list itself.
    const heading =
      root?.querySelector<HTMLElement>("h1") ??
      root?.closest("main")?.querySelector<HTMLElement>("h1");
    if (heading && !heading.hasAttribute("tabindex")) {
      heading.tabIndex = -1;
    }
    const target =
      (focusAfter
        ? root?.querySelector<HTMLElement>(`[data-task-link="${CSS.escape(focusAfter)}"]`)
        : null) ??
      heading ??
      root;
    target?.focus();
  }, [focusAfter]);
  const changeStatus = (task: TaskSummary, next: TaskStatus) => {
    const change: RowStatusChange = {
      projectId: task.projectId,
      taskId: task.id,
      status: next,
      from: task.status,
    };
    const index = tasks.findIndex((x) => x.id === task.id);
    const neighbour = tasks[index + 1] ?? tasks[index - 1];
    // mutateAsync, not mutate's per-call callbacks: those fire for the latest call only, and two
    // rows changed in quick succession each need their own outcome.
    rowStatus.mutateAsync(change).then(
      (saved) => {
        if (statusMatches(statusNow.current, saved.status)) {
          return;
        }
        // The row leaves this list: focus moves on, and Undo can bring it back.
        setFocusAfter(neighbour?.id ?? "");
        const name = t(`tasks.${saved.status}`);
        // The title as loaded, only while neither the write's answer nor a newer read (its own
        // refresh included) has hidden or redacted the task since.
        toast(
          saved.hidden || task.hidden || taskTextRedacted(client, task.projectId, task.id)
            ? t("tasks.rowLeftRestricted", { status: name })
            : t("tasks.rowLeft", { title: task.title, status: name }),
          {
            duration: 8000,
            action: {
              label: t("tasks.rowUndo"),
              onClick: (event) => {
                // One write at a time per task, as the picker: an Undo while one runs waits, and
                // its notice stays (sonner closes an action's toast unless prevented).
                if (pendingNow.current.has(task.id)) {
                  event.preventDefault();
                  toast(t("tasks.rowBusy"));
                  return;
                }
                rowStatus
                  .mutateAsync({ ...change, status: change.from, from: saved.status })
                  .catch((e: unknown) => toast.error(rowError(e)));
              },
            },
          },
        );
      },
      (e: unknown) => toast.error(rowError(e)),
    );
  };
  const rowError = (e: unknown) =>
    writeError(e, (key) => (key === "tasks.uncertain" ? t("tasks.rowUncertain") : t(key)));
  const access = pages[0]?.access;
  const projects = pages.flatMap((p) => ("projects" in p ? (p as MyTaskList).projects : []));
  const filtered =
    status !== "open" || scope !== "assigned" || assignee !== "all" || q.trim() !== "";
  const restart = () => {
    client.resetQueries({ queryKey: taskKeys.list(project?.id ?? null, filters), exact: true });
  };
  return (
    <div
      ref={listRef}
      tabIndex={-1}
      data-esi-content="tasks"
      className="flex flex-col gap-5 outline-none"
    >
      {project ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TaskHeading>{t("tasks.title")}</TaskHeading>
          {access?.canCreate ? (
            <Button size="sm" variant="outline" onClick={() => setCreate(true)}>
              <Plus aria-hidden="true" />
              {t("tasks.newTask")}
            </Button>
          ) : null}
        </div>
      ) : null}
      {access?.writeBlock ? (
        <Notice>
          {t(access.writeBlock === "tasks_disabled" ? "tasks.disabled" : "tasks.frozen")}
        </Notice>
      ) : project && list.data && !access ? (
        <Notice
          action={
            <Button variant="outline" size="sm" onClick={() => void list.refetch()}>
              {t("tasks.refresh")}
            </Button>
          }
        >
          {t("tasks.metadata")}
        </Notice>
      ) : null}
      <div className="flex flex-wrap items-end gap-3">
        {!project ? (
          <TaskSelect
            label={t("tasks.myTitle")}
            value={scope}
            onChange={setScope}
            options={[
              { value: "assigned", label: t("tasks.assigned") },
              { value: "created", label: t("tasks.created") },
            ]}
          />
        ) : (
          <div className="flex min-w-40 flex-1 flex-col gap-1.5">
            <label htmlFor={`task-search-${project.id}`} className="text-sm font-medium">
              {t("tasks.search")}
            </label>
            <Input
              id={`task-search-${project.id}`}
              type="search"
              autoComplete="off"
              maxLength={200}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        )}
        <StatusFilterField value={status} onChange={setStatus} />
        {project && !guest ? (
          <TaskSelect
            label={t("tasks.assignee")}
            value={choosingPerson ? "person" : assignee}
            onChange={(value) => {
              setChoosingPerson(value === "person");
              if (value !== "person") {
                setAssignee(value);
              }
            }}
            options={[
              { value: "all", label: t("tasks.allPeople") },
              { value: "me", label: t("tasks.assigned") },
              { value: "none", label: t("tasks.unassigned") },
              ...(access?.canCreate || assignee === "person" || choosingPerson
                ? [{ value: "person", label: t("tasks.person") }]
                : []),
            ]}
          />
        ) : null}
        {filtered ? (
          <Button
            variant="ghost"
            onClick={() => {
              setStatus("open");
              setScope("assigned");
              setAssignee("all");
              setChoosingPerson(false);
              setPerson(null);
              setQ("");
            }}
          >
            {t("tasks.clearFilters")}
          </Button>
        ) : null}
      </div>
      {project && !guest && (choosingPerson || assignee === "person") ? (
        <div className="max-w-sm">
          <AssigneePicker
            projectId={project.id}
            value={person}
            disabled={!access?.canCreate}
            onChange={(p) => {
              setPerson(p);
              setAssignee(p ? "person" : "none");
              setChoosingPerson(false);
            }}
          />
        </div>
      ) : null}
      {choosingPerson ? <Notice>{t("tasks.selectPersonPrompt")}</Notice> : null}
      {list.error instanceof UnsupportedOpenError ? (
        <Notice
          action={
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => void list.refetch()}>
                {t("app.retry")}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setStatus("all")}>
                {t("tasks.all")}
              </Button>
            </div>
          }
        >
          {t("tasks.unsupportedOpen")}
        </Notice>
      ) : list.isFetchNextPageError ? (
        <Notice
          tone="danger"
          action={
            <Button size="sm" variant="outline" onClick={restart}>
              {t("tasks.restart")}
            </Button>
          }
        >
          {t("tasks.cursorError")}
        </Notice>
      ) : list.isError ? (
        <>
          <TaskError error={list.error} retry={() => void list.refetch()} />
          {tasks.length ? <Notice>{t("tasks.stale")}</Notice> : null}
        </>
      ) : null}
      {list.isPending ? (
        <div
          role="status"
          aria-label={t("app.loading")}
          className="space-y-4 rounded-lg border border-line p-5"
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-10 animate-pulse rounded bg-surface-sunken motion-reduce:animate-none"
            />
          ))}
        </div>
      ) : !list.isError && tasks.length === 0 ? (
        <EmptyState
          icon={ListTodo}
          title={t(filtered ? "tasks.noMatches" : "tasks.empty")}
          action={
            !project ? (
              <Button asChild variant="outline">
                <Link href={PROJECTS_PATH}>{t("tasks.projects")}</Link>
              </Button>
            ) : undefined
          }
        >
          {t(filtered ? "tasks.noMatchesBody" : "tasks.emptyBody")}
        </EmptyState>
      ) : tasks.length ? (
        <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface-raised">
          {tasks.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              busy={pending.has(task.id)}
              onStatus={(next) => changeStatus(task, next)}
              projectName={
                project ? undefined : projects.find((p) => p.id === task.projectId)?.name
              }
            />
          ))}
        </ul>
      ) : null}
      {list.hasNextPage && !list.isFetchNextPageError ? (
        <div>
          <Button
            variant="outline"
            disabled={list.isFetching}
            onClick={() => void list.fetchNextPage()}
          >
            {list.isFetchingNextPage ? t("app.loading") : t("tasks.more")}
          </Button>
        </div>
      ) : null}
      {create && access?.canCreate && project ? (
        <TaskEditor projectId={project.id} onClose={() => setCreate(false)} />
      ) : null}
    </div>
  );
}
/**
 * One task: the title is the row's link (its hit area stretched over the row), the status a
 * sibling cell above it, a picker only where the server says the caller may change it.
 */
export function TaskRow({
  task,
  projectName,
  busy = false,
  onStatus,
}: {
  task: TaskSummary;
  projectName?: string;
  busy?: boolean;
  onStatus?: (status: TaskStatus) => void;
}) {
  const { t } = useTranslation();
  const title = task.hidden ? t("tasks.restricted") : task.title;
  return (
    <li className="group relative flex min-w-0 flex-col gap-2 px-4 py-4 hover:bg-surface-hover has-[[data-task-link]:focus-visible]:outline-2 has-[[data-task-link]:focus-visible]:-outline-offset-2 has-[[data-task-link]:focus-visible]:outline-ring sm:flex-row sm:items-center sm:gap-5 sm:py-3">
      <div className="min-w-0 flex-1">
        <Link
          href={projectTaskPath(task.projectId, task.id)}
          data-task-link={task.id}
          className="line-clamp-2 font-medium break-words text-fg outline-none group-hover:underline after:absolute after:inset-0"
        >
          {title}
        </Link>
        {projectName ? <p className="mt-1 text-xs text-fg-muted">{projectName}</p> : null}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 sm:contents">
        <span className="relative z-10 max-sm:-ml-3">
          {task.canChangeStatus === true && onStatus ? (
            <RowStatusPicker
              status={task.status}
              label={
                task.hidden
                  ? t("tasks.rowStatusRestricted")
                  : t("tasks.rowStatus", { title: task.title })
              }
              busy={busy}
              onChange={onStatus}
            />
          ) : (
            <RowStatusLabel status={task.status} />
          )}
        </span>
        <span className="min-w-0 text-sm break-words text-fg-secondary sm:w-36">
          {task.assignee
            ? (task.assignee.name ?? t("tasks.unavailablePerson"))
            : t("tasks.unassigned")}
        </span>
        <span className="sm:w-44">
          <DueDate due={task.dueDate} status={task.status} />
        </span>
      </div>
    </li>
  );
}
